/**
 * The service card deck: a bar's drinks as cards, in the order a bartender
 * flips through them, with the letters to flip by.
 *
 * Behind the bar the question is "how do I make a Paper Plane", and the
 * answer is one card: the working recipe, as poured. The list on screen 02
 * answers a different question — what the book holds, what it costs, what
 * is retired — and is a manager's tool. This is the arithmetic of the deck
 * and nothing else: which drinks are on it, how they sort, and where each
 * letter starts, so the card itself is all the screen has to draw.
 *
 * Pure. No React, no clock, no storage.
 */
import { houseRecipe, recipeRank } from "./items.js";
export const OTHER_LETTER = "#";
/**
 * Which letter files a name: A–Z, or `#` for anything that does not start
 * with one.
 *
 * Accents are folded first, so "Épices" is filed under E — where the sort
 * puts it, and where a bartender would look for it. Leaving it under `#`
 * split the rail's `#` tab in two: one before "Americano" and another in
 * the middle of the Es.
 */
export function letterOf(name) {
    const first = name.trim().normalize("NFD").replace(/\p{M}/gu, "").charAt(0).toUpperCase();
    return first >= "A" && first <= "Z" ? first : OTHER_LETTER;
}
/**
 * The deck: one card per item that has a recipe, drinks in name order.
 *
 * Sorted by name, the way a card index is, and never by status: a bartender
 * flipping for "Negroni" wants it under N whatever the office has marked
 * it. Which drinks are on the deck at all is the caller's filter.
 *
 * An item with no recipe at all is left off rather than drawn as a card
 * with nothing on it — there is nothing to pour, and a blank card behind
 * the bar is worse than no card.
 */
export function deckOf(items, recipes) {
    const cards = [];
    for (const item of items) {
        const own = recipes.filter((r) => r.itemId === item.itemId);
        if (own.length === 0)
            continue;
        const house = houseRecipe(own, item.itemId);
        const rest = own
            .filter((r) => r !== house)
            .sort((a, b) => recipeRank(a) - recipeRank(b) || a.label.localeCompare(b.label));
        cards.push({
            itemId: item.itemId,
            name: item.name,
            letter: letterOf(item.name),
            recipes: house ? [house, ...rest] : rest,
        });
    }
    return cards.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
}
/**
 * The rail: every letter the deck actually has a drink under, in deck
 * order, each with the first card under it.
 *
 * Only the letters that are there. A rail of twenty-six with four live is a
 * rail you cannot hit at speed, and the whole point of it is speed.
 *
 * One tab per letter, never one per run: a letter that somehow appears in
 * two places in the deck still gets a single tab, pointing at the first.
 * Two tabs reading `#` is a rail nobody can aim at.
 */
export function lettersOf(cards) {
    const first = new Map();
    for (const [index, card] of cards.entries()) {
        if (!first.has(card.letter))
            first.set(card.letter, index);
    }
    return [...first].map(([letter, index]) => ({ letter, index }));
}
//# sourceMappingURL=deck.js.map